import { GRAMMAR_ITEM_POOLS, GRAMMAR_ITEM_SKILLS, GRAMMAR_ITEM_TYPES } from '@repo/config';
import { z } from 'zod';

const grammarAnswerOptionSchema = z.object({
  text: z.string(),
  correct: z.boolean(),
  why: z.string(),
  l1Trap: z.string().optional(),
});

const grammarAnswerOptionsSchema = z
  .array(grammarAnswerOptionSchema)
  .min(2)
  .max(4)
  .refine((options) => options.filter(({ correct }) => correct).length === 1, {
    message: 'Exactly one option must be correct',
  });

const structuredInputPayloadSchema = z.object({
  question: z.string(),
  options: grammarAnswerOptionsSchema,
});

const clozeSentenceSchema = z.string().includes('___');

const grammarPayloadWhySchema = z.string().min(25).max(200);

const clozePayloadSchema = z.union([
  z.object({
    sentence: clozeSentenceSchema,
    options: grammarAnswerOptionsSchema,
    answer: z.never().optional(),
  }),
  z.object({
    sentence: clozeSentenceSchema,
    answer: z.string(),
    acceptedAnswers: z.array(z.string()).min(1).max(6).optional(),
    why: grammarPayloadWhySchema.optional(),
    options: z.never().optional(),
  }),
]);

const reorderPayloadSchema = z.object({
  tokens: z.array(z.string()),
  answer: z.string(),
  why: grammarPayloadWhySchema.optional(),
});

const errorFixPayloadSchema = z.object({
  sentence: z.string(),
  errorSpan: z.string(),
  corrected: z.string(),
  why: z.string(),
  acceptedCorrections: z.array(z.string()).min(1).max(6).optional(),
});

const grammarItemBaseShape = {
  pool: z.enum(GRAMMAR_ITEM_POOLS),
  depth: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  skill: z.enum(GRAMMAR_ITEM_SKILLS).default(GRAMMAR_ITEM_SKILLS[0]),
  prompt: z.string(),
  decisiveCue: z.string().min(1).max(40).nullish(),
  edgeCase: z
    .string()
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
    .optional(),
  hint: z.string().min(12).max(90).nullish(),
  registerContext: z.string().min(12).max(120).nullish(),
  noCueReason: z.enum(['fixed-collocation', 'restatement']).nullish(),
};

const grammarItemSchemaDefinition = z.discriminatedUnion('type', [
  z.object({
    ...grammarItemBaseShape,
    type: z.literal(GRAMMAR_ITEM_TYPES[0]),
    payload: structuredInputPayloadSchema,
  }),
  z.object({
    ...grammarItemBaseShape,
    type: z.literal(GRAMMAR_ITEM_TYPES[1]),
    payload: clozePayloadSchema,
  }),
  z.object({
    ...grammarItemBaseShape,
    type: z.literal(GRAMMAR_ITEM_TYPES[2]),
    payload: reorderPayloadSchema,
  }),
  z.object({
    ...grammarItemBaseShape,
    type: z.literal(GRAMMAR_ITEM_TYPES[3]),
    payload: errorFixPayloadSchema,
  }),
]);

export const grammarItemSchema: typeof grammarItemSchemaDefinition = grammarItemSchemaDefinition;

export type IGrammarItem = z.infer<typeof grammarItemSchema>;
